/**
 * MoistureGuard free sample manager.
 * - Threshold is based only on NON-sample items
 * - If other spend is below threshold and sample is in cart → remove it
 * - If other spend meets threshold and sample is missing → add it
 * - Buy Now / dynamic checkout skips the cart, so those clicks are intercepted,
 *   the product + gift are added, then the shopper is sent to checkout
 */
(() => {
  if (window.__freeSampleGiftInit) return;
  window.__freeSampleGiftInit = true;

  const GIFT_PROPERTY = '_gift';
  const GIFT_VALUE = '1';
  const SAMPLE_VARIANT_ID = 47621969445179;
  const SAMPLE_PRODUCT_ID = 9018685686075;
  const DEFAULT_MINIMUM_CENTS = 2500; // $25

  let pollTimer = null;
  let buyNowLock = false;
  let syncChain = Promise.resolve();

  function routes() {
    return (window.theme && theme.routes) || {};
  }

  function toJsUrl(url) {
    if (!url) return null;
    return `${String(url).replace(/\.js$/i, '').replace(/\/$/, '')}.js`;
  }

  function getConfig() {
    const settings = (window.theme && theme.settings) || {};

    // Feature flag from theme settings; default ON if progress notice exists in DOM
    const notice = document.querySelector('[data-gift-variant][data-minimum]');
    const enabled =
      settings.showFreeSampleGift === true ||
      settings.showFreeSampleGift === 'true' ||
      Boolean(notice);

    if (!enabled) return null;

    const variantId =
      Number(settings.freeSampleGiftVariantId) ||
      Number(notice && notice.getAttribute('data-gift-variant')) ||
      SAMPLE_VARIANT_ID;

    const productId =
      Number(settings.freeSampleGiftProductId) ||
      Number(notice && notice.getAttribute('data-gift-product')) ||
      SAMPLE_PRODUCT_ID;

    const minimum =
      Number(settings.freeSampleMinimum) ||
      Number(notice && notice.getAttribute('data-minimum')) ||
      DEFAULT_MINIMUM_CENTS;

    return { variantId, productId, minimum };
  }

  function isSample(item, config) {
    const vid = Number(item.variant_id);
    const pid = Number(item.product_id);
    return vid === Number(config.variantId) || pid === Number(config.productId);
  }

  function hasGiftProperty(item) {
    const props = item && item.properties;
    if (!props || Array.isArray(props)) {
      if (!Array.isArray(props)) return false;
      const match = props.find((prop) => {
        if (!prop) return false;
        if (Array.isArray(prop)) return String(prop[0]) === GIFT_PROPERTY;
        return prop.name === GIFT_PROPERTY || prop.key === GIFT_PROPERTY;
      });
      if (!match) return false;
      const value = Array.isArray(match) ? match[1] : match.value;
      return String(value) === GIFT_VALUE || String(value) === 'true';
    }
    const value = props[GIFT_PROPERTY];
    return value != null && (String(value) === GIFT_VALUE || String(value) === 'true');
  }

  async function fetchCart() {
    const url = toJsUrl(routes().cart) || '/cart.js';
    const response = await fetch(url, { cache: 'no-store', credentials: 'same-origin' });
    if (!response.ok) throw new Error(`cart.js ${response.status}`);
    return response.json();
  }

  async function postJson(url, body) {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      },
      credentials: 'same-origin',
      body: JSON.stringify(body)
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data.description || data.message || data.errors || String(response.status));
    }
    return data;
  }

  async function refreshUI() {
    const cartItems = document.querySelector('cart-items');
    if (cartItems && typeof cartItems.refresh === 'function') {
      await cartItems.refresh();
      return;
    }
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
  }

  async function addSample(variantId) {
    const url = toJsUrl(routes().cartAdd) || '/cart/add.js';
    await postJson(url, {
      items: [
        {
          id: Number(variantId),
          quantity: 1,
          properties: { [GIFT_PROPERTY]: GIFT_VALUE }
        }
      ]
    });
  }

  async function removeSamples(sampleItems) {
    if (!sampleItems.length) return;

    const updates = {};
    sampleItems.forEach((item) => {
      updates[item.key] = 0;
    });

    const updateUrl = toJsUrl(routes().cartUpdate) || '/cart/update.js';
    try {
      await postJson(updateUrl, { updates });
      return;
    } catch (e) {
      // Fallback: change each line by index (highest first)
    }

    const changeUrl = toJsUrl(routes().cartChange) || '/cart/change.js';
    const cart = await fetchCart();
    const lines = sampleItems
      .map((item) => cart.items.findIndex((row) => row.key === item.key) + 1)
      .filter((line) => line > 0)
      .sort((a, b) => b - a);

    for (const line of lines) {
      await postJson(changeUrl, { line, quantity: 0 });
    }
  }

  async function doSyncGift(options = {}) {
    const refresh = options.refresh !== false;
    const config = getConfig();
    if (!config) return;

    try {
      const cart = await fetchCart();
      const samples = cart.items.filter((item) => isSample(item, config));
      const others = cart.items.filter((item) => !isSample(item, config));
      const spend = others.reduce((sum, item) => sum + Number(item.final_line_price || 0), 0);
      const qualifies = spend >= config.minimum;

      // Under threshold → remove free sample lines
      if (!qualifies && samples.length > 0) {
        let toRemove = [];

        if (others.length > 0) {
          toRemove = samples;
        } else {
          // Only sample left → remove auto-added gift lines, keep a paid MoistureGuard purchase
          toRemove = samples.filter((item) => hasGiftProperty(item));
        }

        if (toRemove.length > 0) {
          await removeSamples(toRemove);
          if (refresh) await refreshUI();
          return;
        }
      }

      // Met threshold and no sample → add it
      if (qualifies && samples.length === 0) {
        await addSample(config.variantId);
        if (refresh) await refreshUI();
        return;
      }

      // Met threshold with multiple sample lines → keep one at qty 1
      if (qualifies && samples.length > 0) {
        const updates = {};
        let changed = false;
        samples.forEach((item, index) => {
          const nextQty = index === 0 ? 1 : 0;
          if (item.quantity !== nextQty) {
            updates[item.key] = nextQty;
            changed = true;
          }
        });
        if (changed) {
          const updateUrl = toJsUrl(routes().cartUpdate) || '/cart/update.js';
          await postJson(updateUrl, { updates });
          if (refresh) await refreshUI();
        }
      }
    } catch (error) {
      console.error('Free sample gift sync error:', error);
      throw error;
    }
  }

  function syncGift(options = {}) {
    const next = syncChain.then(
      () => doSyncGift(options),
      () => doSyncGift(options)
    );
    syncChain = next.catch(() => {});
    return next;
  }

  function getCheckoutUrl() {
    const root = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    return `${root}checkout`;
  }

  function eventPath(event) {
    if (typeof event.composedPath === 'function') {
      try {
        return event.composedPath();
      } catch (e) {
        // Ignore and fall through
      }
    }
    return event.target ? [event.target] : [];
  }

  function isPaymentButtonNode(node) {
    if (!node || node.nodeType !== 1) return false;
    const tag = String(node.tagName || '').toLowerCase();
    if (
      tag === 'shopify-accelerated-checkout' ||
      tag === 'shopify-buy-it-now-button' ||
      tag === 'shopify-accelerated-checkout-cart'
    ) {
      return true;
    }
    if (typeof node.matches !== 'function') return false;
    return node.matches(
      '.shopify-payment-button, .shopify-payment-button__button, [data-shopify="payment-button"]'
    );
  }

  function isProductFormNode(node) {
    if (!node || typeof node.matches !== 'function') return false;
    return node.matches('form.js-product-form, product-form');
  }

  function isProductDynamicCheckoutEvent(event) {
    const path = eventPath(event);
    if (!path.some(isPaymentButtonNode)) return false;
    if (path.some(isProductFormNode)) return true;

    const target = event.target;
    if (!target || typeof target.closest !== 'function') return false;
    return Boolean(
      target.closest('form.js-product-form, product-form') &&
        target.closest(
          '.shopify-payment-button, shopify-accelerated-checkout, [data-shopify="payment-button"]'
        )
    );
  }

  function isCartCheckoutEvent(event) {
    const target = event.target;
    if (!target || typeof target.closest !== 'function') return false;
    if (target.closest('[name="add"]')) return false;
    if (isProductDynamicCheckoutEvent(event)) return false;
    return Boolean(target.closest('[name="checkout"]'));
  }

  function getProductFormFromEvent(event) {
    const path = eventPath(event);
    for (const node of path) {
      if (node && node.matches && node.matches('form.js-product-form')) return node;
    }
    for (const node of path) {
      if (node && typeof node.matches === 'function' && node.matches('product-form')) {
        const form = node.querySelector('form.js-product-form');
        if (form) return form;
      }
    }
    const target = event.target;
    if (!target || typeof target.closest !== 'function') return null;
    const productForm = target.closest('product-form');
    return (
      target.closest('form.js-product-form') ||
      (productForm && productForm.querySelector('form.js-product-form'))
    );
  }

  function formLineItem(form) {
    if (!form) return null;
    const formData = new FormData(form);
    const idInput = form.querySelector('[name="id"]');
    const variantId = Number(formData.get('id') || (idInput && idInput.value));
    if (!variantId) return null;

    const quantity = Math.max(1, Number(formData.get('quantity')) || 1);
    const item = { id: variantId, quantity };
    const sellingPlan = formData.get('selling_plan');
    if (sellingPlan) item.selling_plan = Number(sellingPlan) || sellingPlan;

    const properties = {};
    formData.forEach((value, key) => {
      const match = String(key).match(/^properties\[(.+)\]$/);
      if (match && match[1]) properties[match[1]] = value;
    });
    if (Object.keys(properties).length) item.properties = properties;
    return item;
  }

  function setPaymentButtonBusy(event, busy) {
    const path = eventPath(event);
    const host =
      path.find((node) => node && node.classList && node.classList.contains('shopify-payment-button')) ||
      (event.target && event.target.closest && event.target.closest('.shopify-payment-button'));
    if (!host || !host.style) return;
    host.style.pointerEvents = busy ? 'none' : '';
    host.style.opacity = busy ? '0.65' : '';
  }

  function showBuyNowError(form, message) {
    const productForm = form && form.closest('product-form');
    const errorEl = productForm && productForm.querySelector('.js-form-error');
    if (errorEl) {
      errorEl.hidden = false;
      errorEl.textContent = message;
      return;
    }
    console.error('Buy Now gift checkout error:', message);
  }

  async function addProductThenGiftCheckout(form) {
    const item = formLineItem(form);
    if (!item) throw new Error('Missing product variant');

    const url = toJsUrl(routes().cartAdd) || '/cart/add.js';
    await postJson(url, { items: [item] });
    try {
      await syncGift({ refresh: false });
    } catch (error) {
      console.error('Free sample gift sync error:', error);
    }
    window.location.href = getCheckoutUrl();
  }

  function interceptCheckout(event) {
    if (!getConfig()) return;

    const isProductBuyNow = isProductDynamicCheckoutEvent(event);
    const isCartCheckout = !isProductBuyNow && isCartCheckoutEvent(event);
    if (!isProductBuyNow && !isCartCheckout) return;

    const isNamedCheckout =
      event.target &&
      typeof event.target.closest === 'function' &&
      Boolean(event.target.closest('[name="checkout"]'));
    if (event.type === 'pointerdown' && isNamedCheckout && !isProductBuyNow) return;

    if (isCartCheckout) {
      const terms = document.querySelector('.js-cart-terms-checkbox');
      if (terms && !terms.checked) return;
    }

    event.preventDefault();
    event.stopImmediatePropagation();

    if (buyNowLock) return;
    buyNowLock = true;
    setPaymentButtonBusy(event, true);

    const proceed = isProductBuyNow
      ? addProductThenGiftCheckout(getProductFormFromEvent(event))
      : syncGift({ refresh: false })
          .catch((error) => {
            console.error('Free sample gift sync error:', error);
          })
          .then(() => {
            window.location.href = getCheckoutUrl();
          });

    proceed.catch((error) => {
      buyNowLock = false;
      setPaymentButtonBusy(event, false);
      showBuyNowError(
        getProductFormFromEvent(event),
        error.message || 'Unable to start checkout. Please try Add to cart.'
      );
    });
  }

  function scheduleSync(delay) {
    setTimeout(() => {
      syncGift().catch(() => {});
    }, delay);
  }

  function startPolling() {
    if (pollTimer) return;
    pollTimer = setInterval(() => {
      syncGift().catch(() => {});
    }, 1500);
  }

  function stopPolling() {
    if (!pollTimer) return;
    clearInterval(pollTimer);
    pollTimer = null;
  }

  document.addEventListener('click', interceptCheckout, true);
  document.addEventListener('pointerdown', interceptCheckout, true);

  document.addEventListener('DOMContentLoaded', () => {
    scheduleSync(200);
    startPolling();
  });
  document.addEventListener('on:cart:add', () => scheduleSync(800));
  document.addEventListener('on:line-item:change', () => scheduleSync(200));
  document.addEventListener('on:cart-drawer:before-open', () => {
    scheduleSync(50);
    startPolling();
  });
  document.addEventListener('on:cart-drawer:after-open', () => scheduleSync(300));
  document.addEventListener('on:cart-drawer:after-close', stopPolling);

  // If this script loads after DOMContentLoaded (cached/deferred edge cases)
  if (document.readyState !== 'loading') {
    scheduleSync(200);
    startPolling();
  }
})();
